import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { getSpectreposSiteStatus } from "./plugin.redux";
import { api, apiConfig } from "../api/api";

const POLL_INTERVAL_MS = 30000;

const mask = (value) => {
  if (!value) return "";
  const tail = value.slice(-4);
  return `${"•".repeat(Math.max(8, value.length - 4))}${tail}`;
};

const SpectreposStatusPanel = ({
  siteStatus,
  pluginSettings,
  ownerUsername,
  ownerPassword,
  getSpectreposSiteStatus,
}) => {
  const { t } = useTranslation();
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  const handleCopy = async (value, field) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (_e) {
      // Non-secure context fallback: user can still select manually.
    }
  };

  useEffect(() => {
    getSpectreposSiteStatus();
    const id = setInterval(getSpectreposSiteStatus, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  if (!siteStatus) {
    return <Loading>{t("provision.loading_status", "Loading…")}</Loading>;
  }

  // Tiendas en Chentro (HBS): no hay WP Admin ni llaves de API que mostrar;
  // se entra con SSO desde aquí.
  if (siteStatus.backend === "hbs" && siteStatus.status !== "not_found") {
    return <ChentroShopPanel siteStatus={siteStatus} t={t} />;
  }

  if (siteStatus.status === "not_found") {
    return (
      <Empty>
        {t("provision.no_site", "No SpectrePOS site found for this account.")}
      </Empty>
    );
  }

  if (siteStatus.status === "error") {
    return (
      <ErrorBox>
        <strong>
          {t(
            "provision.status_error_title",
            "Couldn't load store status",
          )}
        </strong>
        <Sub>
          {siteStatus.error ||
            t(
              "provision.status_error_hint",
              "We'll retry automatically. If this keeps happening, contact support.",
            )}
        </Sub>
        <Retry onClick={() => getSpectreposSiteStatus()}>
          {t("provision.retry", "Retry now")}
        </Retry>
      </ErrorBox>
    );
  }

  const { storefront = {}, catalog = {}, stripe = {} } = siteStatus;
  const lastSync = catalog.last_sync_at
    ? moment(catalog.last_sync_at).fromNow()
    : "—";

  return (
    <Wrap>
      {(ownerUsername || ownerPassword) && (
        <CredsCard>
          <CredsHeader>WP Admin sign-in</CredsHeader>
          <CredsHint>
            Save these now — we don't store the password and it won't be
            shown again on a different device.
          </CredsHint>
          <CredsGrid>
            {ownerUsername && (
              <>
                <CredsRowLabel>Username</CredsRowLabel>
                <CredsInput
                  readOnly
                  value={ownerUsername}
                  onFocus={(e) => e.target.select()}
                />
                <CredsActions>
                  <CredsBtn
                    type="button"
                    onClick={() => handleCopy(ownerUsername, "username")}
                  >
                    {copiedField === "username" ? "Copied" : "Copy"}
                  </CredsBtn>
                </CredsActions>
              </>
            )}
            {ownerPassword && (
              <>
                <CredsRowLabel>Password</CredsRowLabel>
                <CredsInput
                  type={showPassword ? "text" : "password"}
                  readOnly
                  value={ownerPassword}
                  onFocus={(e) => e.target.select()}
                />
                <CredsActions>
                  <CredsBtn
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </CredsBtn>
                  <CredsBtn
                    type="button"
                    onClick={() => handleCopy(ownerPassword, "password")}
                  >
                    {copiedField === "password" ? "Copied" : "Copy"}
                  </CredsBtn>
                </CredsActions>
              </>
            )}
          </CredsGrid>
        </CredsCard>
      )}

      <Row>
        <Label>Storefront</Label>
        <Value>
          {storefront.live ? (
            <Badge live>● Live</Badge>
          ) : (
            <Badge>● Coming soon</Badge>
          )}
          <SmallLink
            href={siteStatus.admin_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open admin →
          </SmallLink>
          {siteStatus.site_url && (
            <SmallLink
              href={siteStatus.site_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              View store →
            </SmallLink>
          )}
        </Value>
      </Row>

      <Row>
        <Label>Catalog</Label>
        <Value>
          <strong>{(catalog.product_count || 0).toLocaleString()}</strong>{" "}
          products
          <Sub>Last sync: {lastSync}</Sub>
        </Value>
      </Row>

      <Row>
        <Label>POS</Label>
        <Value>
          {siteStatus.pos_url && (
            <SmallLink
              href={siteStatus.pos_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open POS →
            </SmallLink>
          )}
        </Value>
      </Row>

      <Row>
        <Label>Payments</Label>
        <Value>
          <Badge live>● Cash ready to use</Badge>
          {stripe.connected ? (
            <Badge live>● Stripe connected ({stripe.mode})</Badge>
          ) : (
            <Badge>● Stripe not connected</Badge>
          )}
          {!stripe.connected && (
            <Sub>
              Open the proshop admin → SpectrePOS → Apps → Connect with Stripe.
            </Sub>
          )}
        </Value>
      </Row>

      <Advanced>
        <AdvancedToggle onClick={() => setAdvancedOpen((v) => !v)}>
          {advancedOpen ? "▾" : "▸"} Advanced (API credentials)
        </AdvancedToggle>
        {advancedOpen && (
          <AdvancedBody>
            <CredRow>
              <CredLabel>API URL</CredLabel>
              <CredValue
                readOnly
                value={pluginSettings?.api_url || siteStatus.api_url || ""}
              />
            </CredRow>
            <CredRow>
              <CredLabel>API Key</CredLabel>
              <CredValue readOnly value={mask(pluginSettings?.api_key)} />
            </CredRow>
            <CredRow>
              <CredLabel>API Secret</CredLabel>
              <CredValue readOnly value={mask(pluginSettings?.api_secret)} />
            </CredRow>
          </AdvancedBody>
        )}
      </Advanced>
    </Wrap>
  );
};

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 4px 0 8px;
`;

const Row = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
`;

const Label = styled.div`
  width: 110px;
  font-size: 12px;
  font-weight: 700;
  color: #00197b;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  padding-top: 2px;
  flex-shrink: 0;
`;

const Value = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 14px;
  color: #202124;
`;

const Sub = styled.div`
  font-size: 12px;
  color: #6c757d;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${(props) => (props.live ? "#dafbe1" : "#fff3cd")};
  color: ${(props) => (props.live ? "#1a7f37" : "#7a5b00")};
  width: fit-content;
`;

const SmallLink = styled.a`
  font-size: 12px;
  color: #00197b;
  text-decoration: none;
  margin-right: 12px;
  &:hover {
    text-decoration: underline;
  }
`;

/**
 * Entra a la tienda en Chentro sin clave: Spectre firma un enlace de un solo
 * uso para el usuario de la sesión. La pestaña se abre ANTES del await —
 * Safari bloquea las ventanas que nacen después de una espera.
 */
export const openChentroShop = async (next) => {
  const win = window.open("", "_blank");
  try {
    const res = await api.ajaxSession("getSpectreposSsoUrl", {
      noWorker: true,
      url: apiConfig.appUrl,
      type: "GET",
      params: next ? { next } : {},
    });
    if (!res || !res.url) throw new Error("no url");
    if (win) win.location.href = res.url;
    else window.location.href = res.url;
    return true;
  } catch (_e) {
    if (win) win.close();
    return false;
  }
};

const ChentroShopPanel = ({ siteStatus, t }) => {
  const [failed, setFailed] = useState(false);
  const catalog = siteStatus.catalog || {};
  const ready = siteStatus.status === "ready";
  const open = async (next) => setFailed(!(await openChentroShop(next)));

  if (siteStatus.status === "error") {
    return (
      <ErrorBox>
        <strong>
          {t("provision.status_error_title", "Couldn't load store status")}
        </strong>
        <Sub>
          {siteStatus.error ||
            t(
              "provision.status_error_hint",
              "We'll retry automatically. If this keeps happening, contact support.",
            )}
        </Sub>
      </ErrorBox>
    );
  }

  return (
    <Wrap>
      <Row>
        <Label>{t("provision.store", "Store")}</Label>
        <Value>
          {ready ? (
            <Badge live>● {t("provision.store_ready_short", "Ready")}</Badge>
          ) : (
            <Badge>● {t("provision.setting_up_store", "Setting up your store")}</Badge>
          )}
          {siteStatus.site_url && <Sub>{siteStatus.site_url}</Sub>}
        </Value>
      </Row>
      <Row>
        <Label>{t("provision.catalog", "Catalog")}</Label>
        <Value>
          {catalog.product_count != null ? (
            <>
              <strong>{Number(catalog.product_count).toLocaleString()}</strong>{" "}
              {t("provision.products", "products")}
            </>
          ) : (
            <Sub>{t("provision.step.importing_catalog", "Importing catalog")}</Sub>
          )}
          {catalog.last_status === "error" && (
            <Sub>
              {t(
                "provision.catalog_retry",
                "The catalog import will retry automatically.",
              )}
            </Sub>
          )}
        </Value>
      </Row>
      <Row>
        <Label>{t("provision.open", "Open")}</Label>
        <Value>
          <ChentroButton type="button" disabled={!ready} onClick={() => open()}>
            {t("provision.open_store", "Open my store")}
          </ChentroButton>
          <ChentroButton
            type="button"
            secondary
            disabled={!ready}
            onClick={() => open("/pos/register")}
          >
            {t("provision.open_register", "Open the register")}
          </ChentroButton>
          {failed && (
            <Sub>
              {t(
                "provision.open_failed",
                "We couldn't open your store. Try again in a moment.",
              )}
            </Sub>
          )}
        </Value>
      </Row>
    </Wrap>
  );
};

const ChentroButton = styled.button`
  padding: 8px 14px;
  margin-right: 8px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 6px;
  border: 1px solid #0e7a4d;
  color: ${(props) => (props.secondary ? "#0e7a4d" : "#fff")};
  background: ${(props) => (props.secondary ? "#fff" : "#0e7a4d")};
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const Loading = styled.div`
  font-size: 13px;
  color: #6c757d;
  padding: 12px 0;
`;

const Empty = styled.div`
  font-size: 13px;
  color: #6c757d;
  padding: 12px 0;
  font-style: italic;
`;

const ErrorBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  border: 1px solid #f5c2c7;
  background: #fdf2f2;
  border-radius: 6px;
  font-size: 13px;
  color: #842029;
`;

const Retry = styled.button`
  align-self: flex-start;
  background: #00197b;
  color: #fff;
  border: 0;
  border-radius: 4px;
  padding: 4px 12px;
  font-size: 12px;
  cursor: pointer;
  margin-top: 4px;
  &:hover {
    background: #001563;
  }
`;

const Advanced = styled.div`
  margin-top: 8px;
  border-top: 1px solid #e6eaf5;
  padding-top: 12px;
`;

const AdvancedToggle = styled.button`
  background: none;
  border: 0;
  color: #6c757d;
  font-size: 12px;
  cursor: pointer;
  padding: 0;
  &:hover {
    color: #00197b;
  }
`;

const AdvancedBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
`;

const CredRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CredLabel = styled.div`
  width: 90px;
  font-size: 12px;
  color: #6c757d;
  flex-shrink: 0;
`;

const CredValue = styled.input`
  flex: 1;
  border: 1px solid #d4d4d5;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  font-family: monospace;
  background: #f8f9fa;
  color: #202124;
`;

const CredsCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  background: #fff8e1;
  border: 1px solid #f0c36d;
  border-radius: 6px;
  box-sizing: border-box;
  max-width: 100%;
  overflow: hidden;
`;

const CredsHeader = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #5a4a00;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const CredsHint = styled.div`
  font-size: 12px;
  color: #7a6200;
  margin-top: -4px;
`;

const CredsGrid = styled.div`
  display: grid;
  grid-template-columns: 86px minmax(0, 1fr) auto;
  column-gap: 8px;
  row-gap: 8px;
  align-items: center;
`;

const CredsRowLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #5a4a00;
`;

const CredsInput = styled.input`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  border: 1px solid #d4d4d5;
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 13px;
  font-family: monospace;
  background: #fff;
  color: #202124;
`;

const CredsActions = styled.div`
  display: flex;
  gap: 6px;
`;

const CredsBtn = styled.button`
  padding: 5px 10px;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  background: #00187e;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  &:hover {
    background: #001563;
  }
`;

export default connect(
  (state) => ({
    siteStatus: state.plugin.siteStatus,
    pluginSettings: state.plugin.plugin_settings,
    ownerUsername: state.plugin.ownerUsername,
    ownerPassword: state.plugin.ownerPassword,
  }),
  { getSpectreposSiteStatus },
)(SpectreposStatusPanel);
